import { Link } from "react-router";

import { useDatasetSchema } from "@prequel/import-react";
import {
  Badge,
  BadgeColor,
  DetailCardSection,
  DetailList,
  DetailRow,
  formatDateTime,
  IdValue,
  Table,
  TableChartIcon,
} from "@prequel/shared-components";

import { useAllDestinations } from "@/api/import/destinations";
import { useAllSources } from "@/api/import/sources";
import { useAllStreams } from "@/api/import/streams";
import PipelineCards, { type PipelineCard } from "@/components/import/PipelineCards";
import ProviderCard from "@/components/import/ProviderCard";
import { schemaColumns } from "@/components/import/schemaColumns";
import { getFrequencyLabel } from "@/pages/import/DatasetsPage/DatasetForm/utils";
import { useDatasetContext } from "@/pages/import/DatasetsPage/DatasetPage";
import { getDatasetMethodSummary, getDatasetTypeLabel } from "@/pages/import/DatasetsPage/helpers";
import { getVendorLabel } from "@/pages/import/vendors";

const SCHEMA_COLUMNS = schemaColumns(["Column", "Prequel type", "Source type", "In latest extract"]);

const DatasetOverview = () => {
  const { dataset } = useDatasetContext();
  const { schema, isLoading: isSchemaLoading, error: schemaError } = useDatasetSchema(dataset.id);
  const { items: sources } = useAllSources();
  const { items: destinations } = useAllDestinations();
  const { items: streams, isLoading: isLoadingStreams, error: streamsError } = useAllStreams(dataset.id);

  const source = sources.find((candidate) => candidate.id === dataset.source_id);
  const destinationsByID = new Map(destinations.map((destination) => [destination.id, destination]));
  const title = dataset.name;

  const cards: PipelineCard[] = [
    {
      id: "source",
      label: "Source",
      title: source?.name ?? dataset.source_id,
      subtitle: source && getVendorLabel(source.vendor),
      to: `/import/sources/${dataset.source_id}`,
    },
    {
      id: "dataset",
      label: "Dataset",
      icon: <TableChartIcon className="text-stone-500" />,
      title,
      subtitle: getDatasetMethodSummary(dataset),
      rows: [
        { label: "Type", value: getDatasetTypeLabel(dataset.type) },
        { label: "Frequency", value: getFrequencyLabel(dataset.frequency_minutes) },
        {
          label: "Orchestration",
          value: (
            <Badge
              color={dataset.is_enabled ? BadgeColor.GREEN : BadgeColor.GRAY}
              text={dataset.is_enabled ? "Enabled" : "Disabled"}
            />
          ),
        },
      ],
    },
    {
      id: "streams",
      label: "Streams",
      items: streams.map((stream) => ({
        id: stream.id,
        title: stream.name,
        subtitle: destinationsByID.get(stream.destination_id)?.name ?? stream.destination_id,
        to: `/import/streams/${stream.id}`,
      })),
      // An empty list and a failed one look the same through `items`, so only invite a stream
      // once the request has actually come back empty.
      emptyState: streamsError ? (
        <span className="text-red-600">Failed to load streams.</span>
      ) : isLoadingStreams ? (
        "Loading streams..."
      ) : (
        <Link
          to="/import/streams/new"
          className="
            font-medium text-emerald-600
            hover:underline
          "
        >
          + Add a stream
        </Link>
      ),
    },
  ];

  const schemaRows = (schema ?? []).map((column) => ({
    id: column.column_name,
    cells: [
      { value: <span className="font-mono text-stone-800">{column.column_name}</span> },
      {
        value: (
          <span className="rounded-sm bg-stone-100 px-2 py-0.5 font-mono text-xs text-stone-700">
            {column.prequel_type}
          </span>
        ),
      },
      { value: <span className="font-mono text-xs text-stone-600">{column.source_type}</span> },
      { value: column.in_latest_extract ? "Yes" : "No" },
    ],
  }));

  return (
    <div className="mt-6">
      <PipelineCards cards={cards} />

      <ProviderCard providerId={dataset.provider_id} showDatalake />

      <DetailCardSection title="Details">
        <DetailList>
          <DetailRow label="ID" value={<IdValue id={dataset.id} />} />
          <DetailRow label="Name" value={title} />
          <DetailRow label="Method" value={getDatasetMethodSummary(dataset)} />
          <DetailRow label="Type" value={getDatasetTypeLabel(dataset.type)} />
          <DetailRow label="Frequency" value={getFrequencyLabel(dataset.frequency_minutes)} />
          <DetailRow label="Datalake schema" value={dataset.datalake_schema} />
          <DetailRow label="Datalake table" value={dataset.datalake_table_name} />
          <DetailRow label="Created at" value={formatDateTime(dataset.created_at)} />
          <DetailRow label="Updated at" value={formatDateTime(dataset.updated_at)} />
        </DetailList>
      </DetailCardSection>

      <section className="mb-4">
        <Table
          header={<h3 className="mb-2 text-sm font-medium text-stone-800">Dataset schema</h3>}
          columns={SCHEMA_COLUMNS}
          rows={schemaRows}
          emptyState={
            schemaError
              ? "Failed to load the dataset schema."
              : isSchemaLoading
                ? "Loading schema from the datalake. This process may take a while."
                : "No schema recorded yet. Run an extract to populate it."
          }
        />
      </section>
    </div>
  );
};

export default DatasetOverview;
